import React from 'react';
import { Star, MapPin, Phone, Mail, Building } from 'lucide-react';
import { useQuery } from '@apollo/client';
import Navbar from '../components/Navbar';
import { GET_AGENCIES } from '../graphql/queries/agency';
import type { Agency } from '../types/agency';
import { getImageUrl } from '../utils/imageUrl';
import { getAgencyPageUrl } from '../utils/agencyPageUrl';

export default function AgenciesPage() {
  const { loading, error, data } = useQuery<{ agencies: Agency[] }>(GET_AGENCIES);

  const handleAgencyClick = (agency: Agency) => {
    window.location.href = getAgencyPageUrl(agency);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-32 flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('Erreur de chargement des agences:', error);
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-32 text-center">
          <div className="text-xl text-red-600 mb-2">Une erreur est survenue</div>
          <div className="text-gray-600">{error.message}</div>
        </div>
      </div>
    );
  }

  const agencies = data?.agencies || [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Nos Agences Partenaires</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez nos agences de voyage partenaires, expertes dans l'organisation de voyages inoubliables
            </p>
          </div>

          {agencies.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-gray-600">Aucune agence n'est disponible pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {agencies.map((agency) => (
                <div
                  key={agency.id}
                  onClick={() => handleAgencyClick(agency)}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      {agency.logo ? (
                        <div 
                          style={{ 
                            backgroundColor: agency.theme?.secondaryColor || '#1E40AF',
                          }}
                          className="w-20 h-20 rounded-lg shadow-md flex items-center justify-center p-3"
                        >
                          <img
                            src={getImageUrl(agency.logo)}
                            alt={agency.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div 
                          style={{ backgroundColor: agency.theme?.secondaryColor || '#1E40AF' }}
                          className="w-20 h-20 rounded-lg shadow-md flex items-center justify-center"
                        >
                          <Building className="w-12 h-12 text-white" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-bold">{agency.name}</h2>
                        {agency.verified && (
                          <div className="flex items-center gap-1 text-blue-600 text-sm">
                            <Star className="w-4 h-4 fill-current" />
                            <span>Agence vérifiée</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {agency.description && (
                      <p className="text-gray-600 text-sm mb-6 border-b pb-4">
                        {agency.description}
                      </p>
                    )}

                    <div className="space-y-3 text-gray-600">
                      {agency.address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-5 h-5" />
                          <span>{agency.address}</span>
                        </div>
                      )}
                      {agency.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-5 h-5" />
                          <span>{agency.phone}</span>
                        </div>
                      )}
                      {agency.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-5 h-5" />
                          <span>{agency.email}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-6 flex justify-end">
                      <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                        Voir les voyages
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* <Footer /> */}
    </div>
  );
}